<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import eventService from '../services/event.service'
import categoryService from '../services/category.service'
import coachService from '../services/coach.service'
import playerService from '../services/player.service'
import ParticipantSelectorV2 from '../components/ParticipantSelectorV2.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

// Form data
const title = ref('')
const categoryType = ref('')
const place = ref('')
const description = ref('')
const startDate = ref('') // Read-only
const startTime = ref('')
const endDate = ref('') // Read-only
const endTime = ref('')
const cyclePeriod = ref('')
const cycleEndDate = ref('')
const duration = ref<number | null>(null)

// Participant selection
const selectedCoaches = ref<string[]>([])
const selectedPlayers = ref<string[]>([])
const pendingCategoryUuids = ref<string[]>([])

// Data from API
const categories = ref<any[]>([])
const coaches = ref<any[]>([])
const players = ref<any[]>([])

// Loading states
const loading = ref(true)
const loadingCategories = ref(false)
const loadingCoaches = ref(false)
const loadingPlayers = ref(false)
const submitting = ref(false)

// Get event UUID from route
const eventUuid = computed(() => route.params.uuid as string)
const clubUuid = computed(() => authStore.initData?.club?.uuid || null)

// Category type options
const categoryTypeOptions = [
  { value: 'training', label: t('editEventCycle.typeTraining') },
  { value: 'match', label: t('editEventCycle.typeMatch') },
  { value: 'other', label: t('editEventCycle.typeOther') }
]

// Cycle period options - map backend values to labels
const cyclePeriodOptions = [
  { value: 'DAY', label: t('editEventCycle.frequencyDaily') },
  { value: 'ONE_WEEK', label: t('editEventCycle.frequencyWeekly') },
  { value: 'TWO_WEEK', label: t('editEventCycle.frequencyBiweekly') },
  { value: 'MONTHLY', label: t('editEventCycle.frequencyMonthly') },
  { value: 'YEAR', label: t('editEventCycle.frequencyYearly') }
]

// Fetch cycle event data
const fetchCycleEvent = async () => {
  if (!clubUuid.value || !eventUuid.value) return

  loading.value = true
  try {
    const response = await eventService.getCycleEvent(clubUuid.value, eventUuid.value)

    // Fill form with event data
    title.value = response.title || ''
    categoryType.value = response.category_type || ''
    place.value = response.place || ''
    description.value = response.description || ''
    startDate.value = response.start_date || ''
    startTime.value = response.start_time || ''
    cyclePeriod.value = response.cycle_period || ''
    cycleEndDate.value = response.cycle_end_date || ''
    duration.value = response.duration || null

    // Set endDate to startDate (as they should be the same for cycle events)
    endDate.value = response.start_date || ''

    // Calculate end time from duration
    if (duration.value && startTime.value) {
      const [hours, minutes] = startTime.value.split(':').map(Number)
      const totalMinutes = hours * 60 + minutes + duration.value
      const endHours = Math.floor(totalMinutes / 60)
      const endMinutes = totalMinutes % 60
      endTime.value = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`
    }

    // Set individual participants
    selectedCoaches.value = response.coaches || []
    selectedPlayers.value = response.players || []

    // Store category UUIDs to apply once players/coaches are loaded
    pendingCategoryUuids.value = response.categories || []

  } catch (err: any) {
    console.error('Error fetching cycle event:', err)
    toastStore.error(err.response?.data?.message || t('editEventCycle.errorFetchingCycle'))
  } finally {
    loading.value = false
  }
}

// Fetch categories
const fetchCategories = async () => {
  if (!clubUuid.value) return

  loadingCategories.value = true
  try {
    const response = await categoryService.getCategories(clubUuid.value, 1, 1000)
    categories.value = response.data || []
  } catch (err: any) {
    console.error('Error fetching categories:', err)
    toastStore.error(err.response?.data?.message || t('editEventCycle.errorFetchingCategories'))
  } finally {
    loadingCategories.value = false
  }
}

// Fetch coaches
const fetchCoaches = async () => {
  if (!clubUuid.value) return

  loadingCoaches.value = true
  try {
    const response = await coachService.getCoaches(clubUuid.value, 9999)
    coaches.value = response.data || []
  } catch (err: any) {
    console.error('Error fetching coaches:', err)
    toastStore.error(err.response?.data?.message || t('editEventCycle.errorFetchingCoaches'))
  } finally {
    loadingCoaches.value = false
  }
}

// Fetch all players
const fetchPlayers = async () => {
  if (!clubUuid.value) return

  loadingPlayers.value = true
  try {
    const response = await playerService.getPlayers(clubUuid.value, { per_page: 9999 })
    players.value = response.data?.players || []
  } catch (err: any) {
    console.error('Error fetching players:', err)
  } finally {
    loadingPlayers.value = false
  }
}

// Handle form submission
const handleSubmit = async () => {
  if (!clubUuid.value || !eventUuid.value) {
    toastStore.error(t('editEventCycle.errorNoClub'))
    return
  }

  if (!title.value || !categoryType.value) {
    toastStore.error(t('editEventCycle.errorFillRequired'))
    return
  }

  submitting.value = true

  try {
    const data: any = {
      title: title.value,
      category_type: categoryType.value,
      place: place.value || null,
      description: description.value.trim() || null,
      start_time: startTime.value || null,
      end_time: endTime.value || null,
      cycle_period: cyclePeriod.value || null,
      cycle_end_date: cycleEndDate.value || null,
      coaches: selectedCoaches.value,
      players: selectedPlayers.value
    }

    const response = await eventService.updateCycleEvent(clubUuid.value, eventUuid.value, data)
    const message = response?.message || t('editEventCycle.cycleUpdated')
    toastStore.success(message)

    // Redirect back to cycles list
    router.push('/events/cycles')
  } catch (err: any) {
    console.error('Error updating cycle event:', err)
    const errorMessage = err.response?.data?.message || t('editEventCycle.errorUpdatingCycle')
    toastStore.error(errorMessage)
  } finally {
    submitting.value = false
  }
}

const applyPendingCategories = () => {
  if (!pendingCategoryUuids.value.length) return
  pendingCategoryUuids.value.forEach(categoryUuid => {
    players.value
      .filter(p => p.categories?.some((c: any) => c.uuid === categoryUuid))
      .forEach(p => { if (!selectedPlayers.value.includes(p.uuid)) selectedPlayers.value.push(p.uuid) })
    coaches.value
      .filter(c => c.categories?.some((c2: any) => c2.uuid === categoryUuid))
      .forEach(c => { if (!selectedCoaches.value.includes(c.uuid)) selectedCoaches.value.push(c.uuid) })
  })
  pendingCategoryUuids.value = []
}

// Initialize
onMounted(async () => {
  await Promise.all([
    fetchCycleEvent(),
    fetchCategories(),
    fetchCoaches(),
    fetchPlayers()
  ])
  applyPendingCategories()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-4 py-2">
      <!-- Back Button -->
      <div class="mb-4">
        <router-link
          to="/events/cycles"
          class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('eventCycles.backToCyclesList') }}
        </router-link>
      </div>

      <!-- Page Header -->
      <div class="mb-4 md:mb-4 mb-2">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('editEventCycle.title') }}</h1>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p class="mt-2 text-gray-600">{{ t('editEventCycle.loadingCycle') }}</p>
      </div>

      <!-- Form -->
      <div v-if="!loading" class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
        <form @submit.prevent="handleSubmit">
          <!-- Basic Fields -->
          <div class="mb-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ t('editEventCycle.basicInfo') }}</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Event Name -->
              <div class="md:col-span-2">
                <label for="eventName" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editEventCycle.eventName') }} <span class="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="eventName"
                  v-model="title"
                  required
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Event Type -->
              <div>
                <label for="categoryType" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editEventCycle.eventType') }} <span class="text-red-600">*</span>
                </label>
                <select
                  id="categoryType"
                  v-model="categoryType"
                  required
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600 bg-white"
                >
                  <option value="">{{ t('editEventCycle.selectType') }}</option>
                  <option v-for="option in categoryTypeOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <!-- Place -->
              <div>
                <label for="place" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editEventCycle.place') }}
                </label>
                <input
                  type="text"
                  id="place"
                  v-model="place"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Cycle End Date -->
              <div class="md:col-span-2">
                <label for="cycleEndDate" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editEventCycle.cycleEndDate') }}
                </label>
                <input
                  type="date"
                  id="cycleEndDate"
                  v-model="cycleEndDate"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Start Date (disabled) -->
              <div>
                <label for="startDate" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editEventCycle.startDate') }}
                </label>
                <input
                  type="date"
                  id="startDate"
                  v-model="startDate"
                  disabled
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded bg-gray-100 text-gray-600 cursor-not-allowed"
                >
              </div>

              <!-- Start Time -->
              <div>
                <label for="startTime" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editEventCycle.startTime') }}
                </label>
                <input
                  type="time"
                  id="startTime"
                  v-model="startTime"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- End Date (disabled) -->
              <div>
                <label for="endDate" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editEventCycle.endDate') }}
                </label>
                <input
                  type="date"
                  id="endDate"
                  v-model="endDate"
                  disabled
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded bg-gray-100 text-gray-600 cursor-not-allowed"
                >
              </div>

              <!-- End Time -->
              <div>
                <label for="endTime" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editEventCycle.endTime') }}
                </label>
                <input
                  type="time"
                  id="endTime"
                  v-model="endTime"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Frequency -->
              <div class="md:col-span-2">
                <label for="cyclePeriod" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editEventCycle.frequency') }}
                </label>
                <select
                  id="cyclePeriod"
                  v-model="cyclePeriod"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600 bg-white"
                >
                  <option v-for="option in cyclePeriodOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <!-- Description -->
              <div class="md:col-span-2">
                <label for="description" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editEventCycle.description') }}
                </label>
                <textarea
                  id="description"
                  v-model="description"
                  rows="4"
                  maxlength="5000"
                  :placeholder="t('editEventCycle.descriptionPlaceholder')"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                ></textarea>
                <p class="mt-1 text-xs text-gray-500">{{ t('editEventCycle.descriptionCycleHint') }}</p>
              </div>
            </div>
          </div>

          <!-- Participants Section -->
          <div class="mb-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ t('editEventCycle.selectParticipants') }}</h2>
            <div v-if="loadingCategories || loadingCoaches || loadingPlayers" class="flex items-center gap-2 py-2 text-sm text-gray-500">
              <div class="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
              Ładowanie uczestników...
            </div>
            <ParticipantSelectorV2
              v-else
              :categories="categories"
              :players="players"
              :coaches="coaches"
              v-model:selectedPlayers="selectedPlayers"
              v-model:selectedCoaches="selectedCoaches"
            />
          </div>

          <!-- Submit Button -->
          <div class="flex justify-end gap-2">
            <router-link
              to="/events/cycles"
              class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
            >
              {{ t('editEventCycle.cancel') }}
            </router-link>
            <button
              type="submit"
              :disabled="submitting"
              class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 border-2 border-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ submitting ? t('editEventCycle.saving') : t('editEventCycle.saveChanges') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </main>
</template>
